<script context="module">
  let idCounter = 0
  function getId () {
    return 'sc' + idCounter++
  }
</script>

<script>
  import { beforeUpdate } from 'svelte'
  import detectIt from 'detect-it'
  import * as GraphicContext from '../Graphic/GraphicContext'
  import * as SectionContext from './SectionContext'
  import * as CoordinateTransformationContext from './CoordinateTransformationContext'
  import * as EventManagerContext from '../Graphic/EventManagerContext'
  import * as InteractionManagerContext from './InteractionManagerContext'
  import * as ZoomContext from './ZoomContext'

  import InteractionManager from '../../../interactivity/interactions/InteractionManager'
  import { scaleCoordinates } from '../../Marks/Rectangle/createCoordSysGeometry.js'
  import { parsePadding, applyPadding } from '../utils/padding.js'

  let sectionId = getId()
  
  // Props
  export let x1 = undefined
  export let x2 = undefined
  export let y1 = undefined
  export let y2 = undefined
  export let padding = 0
  export let scaleX = undefined
  export let scaleY = undefined
  export let flipX = false
  export let flipY = false
  export let zoomIdentity = undefined
  export let transformation = undefined
  export let blockReindexing = false

  // Aesthetics
  export let backgroundColor = undefined
  export let paddingColor = undefined

  // Mouse interactions
  export let onWheel = undefined
  export let onClick = undefined
  export let onMousedown = undefined
  export let onMouseup = undefined
  export let onMouseover = undefined
  export let onMouseout = undefined
  export let onMousemove = undefined
  
  // Touch interactions
  export let onTouchstart = undefined
  export let onTouchmove = undefined
  export let onTouchend = undefined

  // Contexts
  const graphicContext = GraphicContext.subscribe()
  const sectionContext = SectionContext.subscribe()
  const newSectionContext = SectionContext.init()
  const coordinateTransformationContext = CoordinateTransformationContext.subscribe()
  const newCoordinateTransformationContext = CoordinateTransformationContext.init()
  const eventManagerContext = EventManagerContext.subscribe()
  const interactionManagerContext = InteractionManagerContext.init()
  const zoomContext = ZoomContext.init()
  
  let scaledCoordinates
  let rangeX
  let rangeY
  
  // Set up InteractionManager
  let interactionManager = new InteractionManager()
  interactionManager.setId(sectionId)
  interactionManager.linkEventManager($eventManagerContext)
  InteractionManagerContext.update(interactionManagerContext, interactionManager)

  // Keep SectionContext and CoordinateTransformationContext up to date
  let _padding

  $: {
    scaledCoordinates = scaleCoordinates({ x1, x2, y1, y2 }, $sectionContext)
    rangeX = [scaledCoordinates.x1, scaledCoordinates.x2]
    rangeY = [scaledCoordinates.y1, scaledCoordinates.y2]
    
    if (flipX) rangeX.reverse()
    if (flipY) rangeY.reverse()

    _padding = parsePadding(padding)
    rangeX = applyPadding(rangeX, _padding.left, _padding.right)
    rangeY = applyPadding(rangeY, _padding.top, _padding.bottom)

    const updatedSectionContext = { 
      sectionId, rangeX, rangeY, scaleX, scaleY, padding: _padding, flipX, flipY, blockReindexing
    }

    SectionContext.update(
      newSectionContext, updatedSectionContext
    )

    CoordinateTransformationContext.update(
      newCoordinateTransformationContext, { rangeX, rangeY, transformation }
    )

    $interactionManagerContext.loadSection($newSectionContext)
    $interactionManagerContext.loadCoordinateTransformation($newCoordinateTransformationContext)
  }

  // Change callbacks if necessary
  $: {
    removeSectionInteractionsIfNecessary(
      onWheel, onClick, onMousedown, onMouseup, onMouseover, onMouseout
    )
  }

  // Update zooming and panning
  $: {
    ZoomContext.update(zoomContext, zoomIdentity)
    $interactionManagerContext.loadZoom($zoomContext)
  }

  beforeUpdate(() => {
    CoordinateTransformationContext.ensureNotParent($coordinateTransformationContext)
  })

  function removeSectionInteractionsIfNecessary () {
    if (detectIt.hasMouse) {
      const sectionInterface = $interactionManagerContext.mouse().section()
      sectionInterface.removeAllInteractions()

      if (onWheel) sectionInterface.addInteraction('wheel', onWheel)
      if (onClick) sectionInterface.addInteraction('click', onClick)
      if (onMousedown) sectionInterface.addInteraction('mousedown', onMousedown)
      if (onMouseup) sectionInterface.addInteraction('mouseup', onMouseup)
      if (onMouseover) sectionInterface.addInteraction('mouseover', onMouseover)
      if (onMouseout) sectionInterface.addInteraction('mouseout', onMouseout)
      if (onMousemove) sectionInterface.addInteraction('mousemove', onMousemove)
    }

    if (detectIt.hasTouch) {
      const sectionInterface = $interactionManagerContext.touch().section()
      sectionInterface.removeAllInteractions()

      if (onTouchstart) sectionInterface.addInteraction('touchstart', onTouchstart)
      if (onTouchmove) sectionInterface.addInteraction('touchmove', onTouchmove)
      if (onTouchend) sectionInterface.addInteraction('touchend', onTouchend)
    }
  }

  // Selection API
  const selectManager = $interactionManagerContext.select()

  export function selectRectangle (rectangle) {
    selectManager.selectRectangle(rectangle)
  }

  export function updateSelectRectangle (rectangle) {
    selectManager.updateSelectRectangle(rectangle)
  }

  export function resetSelectRectangle () {
    selectManager.resetSelectRectangle()
  }

  export function startSelectPolygon (startCoordinates) {
    selectManager.startSelectPolygon(startCoordinates)
  }

  export function addPointToSelectPolygon (pointCoordinates) {
    selectManager.addPointToSelectPolygon(pointCoordinates)
  }

  export function moveSelectPolygon (delta) {
    selectManager.moveSelectPolygon(delta)
  }

  export function getSelectPolygon () {
    return selectManager.getSelectPolygon()
  }

  export function resetSelectPolygon () {
    selectManager.resetSelectPolygon()
  }
</script>

<defs>
  <clipPath id={`clip-${sectionId}`}>
    <rect 
      x={Math.min(scaledCoordinates.x1, scaledCoordinates.x2)}
      y={Math.min(scaledCoordinates.y1, scaledCoordinates.y2)}
      width={Math.abs(scaledCoordinates.x2 - scaledCoordinates.x1)}
      height={Math.abs(scaledCoordinates.y2 - scaledCoordinates.y1)}
    />
  </clipPath>
  <clipPath id={`clip-${sectionId}-data`}>
    <rect 
      x={Math.min(...rangeX)}
      y={Math.min(...rangeY)}
      width={Math.abs(rangeX[0] - rangeX[1])}
      height={Math.abs(rangeY[0] - rangeY[1])}
      fill="white"
    />
  </clipPath>
</defs>

<g class="section" clip-path={`url(#clip-${sectionId})`} >
  {#if paddingColor}
    <rect class="padding-background"
      mask={`url(#clip-${sectionId}-data)`}
      width="100%"
      height="100%" 
      fill={paddingColor}
    />
  {/if}
  {#if backgroundColor}
    <rect class="content-background"
      x={Math.min(...rangeX)}
      y={Math.min(...rangeY)}
      width={Math.abs(rangeX[0] - rangeX[1])}
      height={Math.abs(rangeY[0] - rangeY[1])}
      fill={backgroundColor}
    />
  {/if}
  <slot />
</g>